// eTIMS signing (ADR 0041, docs/plans/phase3-etims-vscu.md): the VSCU offline allowance and
// the state a person sees. Pure; the API reads the backlog and the configured limits.

const HOUR_MS = 3_600_000;

/**
 * How long and how much a VSCU may sign without reaching KRA. RESEARCH.md §5.2 says "about 24
 * hours"; whether there is a document count, and what the hours count from, is
 * TODO(verify-spec) (plan VSCU-4). Deployment configuration, never a facility setting.
 */
export interface VscuOfflineLimits {
  readonly maxHours: number;
  /** Null: no document-count limit. */
  readonly maxDocuments: number | null;
}

/** The facility's documents signed offline and not yet sent to KRA. */
export interface VscuOfflineBacklog {
  readonly count: number;
  /** When the oldest of them was signed; null when there are none. */
  readonly oldestSignedAt: Date | null;
}

export type VscuOfflineAllowance =
  | {
      readonly allowed: true;
      /** When offline signing stops unless the backlog reaches KRA; null with no backlog. */
      readonly signingStopsAt: Date | null;
      /** Null when there is no document-count limit. */
      readonly documentsLeft: number | null;
    }
  | {
      readonly allowed: false;
      /** Which limit was reached; hours first when both are. */
      readonly limit: 'hours' | 'documents';
    };

/**
 * Whether the VSCU may sign one more document now (plan V6): refused once the oldest unsent
 * document is `maxHours` old, or once `maxDocuments` are waiting.
 */
export function vscuOfflineAllowance(
  backlog: VscuOfflineBacklog,
  limits: VscuOfflineLimits,
  now: Date,
): VscuOfflineAllowance {
  if (!Number.isFinite(limits.maxHours) || limits.maxHours <= 0)
    throw new Error('maxHours must be a positive number');
  if (
    limits.maxDocuments !== null &&
    (!Number.isInteger(limits.maxDocuments) || limits.maxDocuments < 1)
  )
    throw new Error('maxDocuments must be a whole number of at least 1');
  if (backlog.count < 0 || backlog.count > 0 !== (backlog.oldestSignedAt !== null))
    throw new Error('a backlog has an oldest document exactly when it is not empty');

  const stopsAt =
    backlog.oldestSignedAt === null
      ? null
      : new Date(backlog.oldestSignedAt.getTime() + limits.maxHours * HOUR_MS);
  if (stopsAt !== null && now.getTime() >= stopsAt.getTime())
    return { allowed: false, limit: 'hours' };
  if (limits.maxDocuments !== null && backlog.count >= limits.maxDocuments)
    return { allowed: false, limit: 'documents' };
  return {
    allowed: true,
    signingStopsAt: stopsAt,
    documentsLeft: limits.maxDocuments === null ? null : limits.maxDocuments - backlog.count,
  };
}

/** A document's signing state (`fiscal_status`), as stored. */
export const FISCAL_STATUSES = [
  'not_configured',
  'queued',
  'signed',
  'signed_offline',
  'failed',
  'transmission_failed',
] as const;
export type FiscalStatus = (typeof FISCAL_STATUSES)[number];

/**
 * The state a person sees (plan §4): a document still waiting whose signing event has run
 * out of retries is shown as failed at once, before the reconciliation job writes it.
 */
export function shownFiscalStatus(
  stored: FiscalStatus,
  outboxStatus: 'pending' | 'retrying' | 'sent' | 'dead' | null,
): FiscalStatus {
  return stored === 'queued' && outboxStatus === 'dead' ? 'failed' : stored;
}

/** Whether nothing more is expected for the document: signed and with KRA, or eTIMS off. */
export function fiscalStatusIsSettled(status: FiscalStatus): boolean {
  return status === 'signed' || status === 'not_configured';
}
